// glonet.ai voice onboarding/update — inline ElevenLabs session (@elevenlabs/client).
//
// Flow states:
//   starting -> live -> ended (countdown to close, "Don't close" -> post)
//   post: offers "Update objective / offering / both" -> restarts the session
//         with update_intent so the agent skips the which-field question.
// Live extras:
//   - thinking indicator (user finished speaking, agent hasn't replied yet)
//   - client tools the agent can call: show_matching_info (view panel),
//     open_editor(field) (manual edit panel; Save writes through the existing
//     PUT events/{id}/update path, which also re-queues matching).
// Each run refetches the member's current info so has_profile / prev_* dynamic
// variables are fresh. user_id rides along for the post-call webhook.
//
// anonymous mode (v2 landing "talk first, sign in after"): no auth, no API —
// prev_* comes from the localStorage draft, save_matching_info/manual edits
// write the draft back, user_id is empty (the post-call webhook skips those),
// and the session is hard-capped at ANON_MAX_MS so a curious stranger can't
// hold an ElevenLabs line open indefinitely. The parent shows the "where
// should we send your matches?" sign-in prompt when the session ends.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Conversation } from "@elevenlabs/client";
import api from "../services/api";
import { API_PATHS } from "../config/apiConfig";
import { GLONET_AI_EVENT_ID, GLONET_VOICE_AGENT_ID } from "../config/glonetai";
import { readVoiceDraft, writeVoiceDraft } from "../utils/voiceDraft";
import MicSelector, { GA_MIC_DEVICE_KEY } from "./MicSelector";
import log from "../utils/logger";
import {
  gvTel, gvTelReset, gvMark, gvTimeSpan, gvTimedTools, gvModeRecorder,
} from "../utils/voiceTelemetry";

const ACCENT = "#74a0f5";
const MONO = "'JetBrains Mono',monospace";
const CLOSE_SECONDS = 8;
const ANON_MAX_MS = 4 * 60 * 1000; // anonymous sessions cost real credits — hard stop

// The agent speaks its first message verbatim ({{greeting}}), so the frontend —
// which knows the member's state and which button they clicked — composes the
// right opening. Action-first: greet, then immediately drive the next step.
function composeGreeting(info, intent, anonymous) {
  if (!info || !info.has_profile) {
    return anonymous
      // honest about the one step left: the matchmaker starts after sign-in
      ? "Welcome to Glonet. Two quick things, then sign in and the matchmaker gets to work — first: what are you looking for right now?"
      : "Welcome to Glonet. Two quick things and the matchmaker gets to work — first: what are you looking for right now?";
  }
  const menu = "I can read your current one, put it on screen, or just take the new version. Which would you like?";
  if (intent === "objective") return `Welcome back. Let's update your objective — ${menu}`;
  if (intent === "offering") return `Welcome back. Let's update your offering — ${menu}`;
  if (intent === "both") return `Welcome back — let's update both, starting with your objective. ${menu}`;
  return "Welcome back. Your matching info is on file — want to update your objective, your offering, or both?";
}

const panelStyle = {
  marginTop: 14, padding: "14px 16px", background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, textAlign: "left",
};
const pillBtn = (primary) => ({
  fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer",
  padding: "8px 16px", borderRadius: 999,
  color: primary ? "#0c0d10" : "#f1f2f4",
  background: primary ? ACCENT : "transparent",
  border: primary ? "none" : "1px solid rgba(255,255,255,0.18)",
});

function Bars({ active }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, height: 22, flex: 1 }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <span key={i} style={{
          flex: 1, minWidth: 3, height: 22, borderRadius: 3,
          background: active ? ACCENT : "rgba(255,255,255,0.15)",
          transformOrigin: "center",
          animation: active ? `ga-wave 0.7s ease-in-out ${(i % 5) * 0.09}s infinite` : "none",
        }} />
      ))}
    </div>
  );
}

function ThinkingDots() {
  return (
    <span style={{ display: "inline-flex", gap: 3, marginLeft: 6 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 4, height: 4, borderRadius: "50%", background: ACCENT,
          animation: `ga-wave 1s ease-in-out ${i * 0.18}s infinite`,
        }} />
      ))}
    </span>
  );
}

export default function VoiceOnboarding({ agentId = GLONET_VOICE_AGENT_ID, initialIntent = "", onEnd, micSilenceSecs = 5, anonymous = false }) {
  const [phase, setPhase] = useState("starting"); // starting | live | ended | post | error
  const [errMsg, setErrMsg] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [heardTick, setHeardTick] = useState(0); // last user transcript — proof the agent hears the mic
  const [profile, setProfile] = useState(null); // {objective, offering, has_profile,...}
  const [panel, setPanel] = useState(null); // null | {mode:'view'} | {mode:'edit', field, text}
  const [panelErr, setPanelErr] = useState(""); // save errors shown INSIDE the panel
  const [panelSaved, setPanelSaved] = useState(false); // brief "Saved ✓" confirmation
  const [countdown, setCountdown] = useState(CLOSE_SECONDS);
  const [runSeq, setRunSeq] = useState(0);
  const intentRef = useRef(initialIntent);

  // The agent's `field` argument is free text — it has arrived as "Offering",
  // "offer", or missing entirely. A strict === "offering" check silently fell
  // back to the objective (and the mismatched raw value then failed BOTH
  // comparisons in the manual save, discarding the edit). Normalize once here.
  const normalizeField = (raw) => {
    // A single-field session intent (the button the member clicked) is
    // AUTHORITATIVE — the agent repeatedly passes "objective" when the member
    // is updating the offering. Only trust the agent's field when the session
    // is open-ended ('' or 'both').
    const intent = String(intentRef.current || "").toLowerCase();
    if (intent === "offering" || intent === "objective") return intent;
    const s = String(raw || "").toLowerCase();
    if (s.includes("offer")) return "offering";
    if (s.includes("object") || s.includes("goal")) return "objective";
    return "objective";
  };
  const sessionRef = useRef(null);
  const anonTimerRef = useRef(null);
  const endedByUserRef = useRef(false);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  // --- session lifecycle (one per runSeq) ---
  useEffect(() => {
    let cancelled = false;
    setPhase("starting"); setPanel(null); setSpeaking(false); setThinking(false);
    // Session zero for the timings the happy-path flow docs report.
    gvTelReset(`onboarding run ${runSeq} intent=${intentRef.current || "(none)"}`);
    (async () => {
      try {
        let me, info;
        if (anonymous) {
          // no account yet — "profile" is the localStorage draft (may be a
          // returning visitor who talked but never signed in)
          const draft = readVoiceDraft();
          me = { user_id: "" }; // post-call webhook skips empty user_id
          info = {
            objective: (draft && draft.objective) || "",
            offering: (draft && draft.offering) || "",
            has_profile: !!(draft && (draft.objective || draft.offering)),
          };
        } else {
          [{ data: me }, { data: info }] = await Promise.all([
            api.get(API_PATHS.USERS.PROFILE),
            api.get(API_PATHS.EVENTS.MY_MATCHING_INFO.replace("{eventId}", GLONET_AI_EVENT_ID)),
          ]);
        }
        if (cancelled) return;
        gvMark("profile_loaded");
        setProfile(info);
        await navigator.mediaDevices.getUserMedia({ audio: true });
        gvMark("mic_granted");
        // Bluetooth headsets switch audio profiles (A2DP->HFP) when the mic engages,
        // swallowing ~1-2s of output — without this pause the agent's greeting plays
        // into the gap and is never heard.
        await new Promise((r) => setTimeout(r, 1200));
        if (cancelled) return;
        // saved mic choice (from the silent-mic selector), self-healing when
        // the device has since disappeared — same pattern as the game's VoiceDM
        let mic = localStorage.getItem(GA_MIC_DEVICE_KEY) || "";
        if (mic) {
          try {
            const devs = await navigator.mediaDevices.enumerateDevices();
            if (!devs.some((d) => d.kind === "audioinput" && d.deviceId === mic)) {
              localStorage.removeItem(GA_MIC_DEVICE_KEY);
              mic = "";
            }
          } catch (e) { /* enumeration unavailable — try it anyway */ }
        }
        const recordMode = gvModeRecorder("onboarding");
        const session = await gvTimeSpan("session_connect", () => Conversation.startSession({
          agentId,
          connectionType: "websocket",
          ...(mic ? { inputDeviceId: mic } : {}),
          dynamicVariables: {
            user_id: me.user_id,
            has_profile: info.has_profile ? "true" : "false",
            prev_objective: info.objective || "",
            prev_offering: info.offering || "",
            update_intent: intentRef.current || "",
            // stored readiness gaps -> the agent gently presses for these;
            // empty on a first visit so the low-friction first pass is untouched
            readiness_hint: (info.readiness_missing || []).join("; "),
            greeting: composeGreeting(info, intentRef.current, anonymous),
          },
          clientTools: gvTimedTools("onboarding", {
            show_matching_info: async (args) => {
              // Re-fetch at show time — the session-start snapshot goes stale if
              // anything (webhook, another tab) wrote after the call began.
              // (anonymous: there is nothing server-side to fetch — the local
              // draft state IS the truth)
              if (!anonymous) {
                try {
                  const { data } = await api.get(
                    API_PATHS.EVENTS.MY_MATCHING_INFO.replace("{eventId}", GLONET_AI_EVENT_ID));
                  setProfile((prev) => ({ ...(prev || {}), ...data }));
                } catch (e) { /* show the snapshot we have */ }
              }
              // Which field to display: a single-field session intent wins;
              // otherwise the agent's argument; otherwise both.
              const intent = String(intentRef.current || "").toLowerCase();
              let field = "both";
              if (intent === "objective" || intent === "offering") field = intent;
              else {
                const raw = String((args && args.field) || "").toLowerCase();
                if (raw.includes("offer")) field = "offering";
                else if (raw.includes("object") || raw.includes("goal")) field = "objective";
              }
              setPanel({ mode: "view", field });
              return field === "both"
                ? "displayed on screen (current objective and offering)"
                : `displayed on screen (the member's current ${field} only)`;
            },
            open_editor: (args) => {
              const field = normalizeField(args && args.field);
              setPanel({
                mode: "edit",
                field,
                returnField: field,
                text: (field === "offering" ? (profileRef.current?.offering) : (profileRef.current?.objective)) || "",
              });
              // name the field back to the agent so it can correct itself if
              // it meant the other one
              return `editor opened for the member's ${field}; they will edit and save on screen`;
            },
            // Instant, authenticated save the moment the member confirms — no
            // webhook wait. Registers first-timers (free tier was verified
            // before the session started); updates existing attendees. The
            // post-call webhook stays as fallback if the browser dies mid-call.
            save_matching_info: async ({ objective, offering }) => {
              const cur = profileRef.current || {};
              // Single-field intent is authoritative: the agent regularly labels
              // the new OFFERING text as "objective" (and vice versa). Route the
              // provided text into the intended field and never touch the other.
              const intent = String(intentRef.current || "").toLowerCase();
              if (intent === "offering") {
                offering = offering || objective || "";
                objective = "";
              } else if (intent === "objective") {
                objective = objective || offering || "";
                offering = "";
              }
              const merged = {
                objective: objective !== undefined && objective !== "" ? objective : (cur.objective || ""),
                offering: offering !== undefined && offering !== "" ? offering : (cur.offering || ""),
              };
              // anonymous: park it in the draft — the landing writes it through
              // the real register/update path right after sign-in. The result
              // text steers the agent's closing line (tool results are LLM
              // turns): no account yet, so it must NOT promise the matchmaker
              // is already working.
              if (anonymous) {
                writeVoiceDraft(merged);
                setProfile((prev) => ({ ...(prev || {}), ...merged, has_profile: true }));
                return "saved on this device — the visitor has no account yet, so no "
                  + "matches can be sent. Tell them briefly: answers saved; create an "
                  + "account or sign in on screen so the matchmaker can take it from there.";
              }
              try {
                if (cur.is_attendee) {
                  await api.put(API_PATHS.EVENTS.UPDATE.replace("{eventId}", GLONET_AI_EVENT_ID), merged);
                } else {
                  await api.post(
                    API_PATHS.EVENTS.REGISTER
                      .replace("{eventId}", GLONET_AI_EVENT_ID)
                      .replace("{invitation_code}", cur.invitation_code),
                    merged);
                }
                setProfile((prev) => ({ ...(prev || {}), ...merged, is_attendee: true, has_profile: true }));
                return "saved successfully — profile updated";
              } catch (err) {
                log.error("save_matching_info failed", { error: err });
                return "save failed — the webhook fallback will save it automatically within a minute";
              }
            },
          }),
          onModeChange: ({ mode }) => {
            recordMode(mode);
            setSpeaking(mode === "speaking");
            if (mode === "speaking") setThinking(false);
          },
          onMessage: ({ source }) => {
            // user just finished an utterance -> agent is "thinking" until it replies
            gvTel("message", { surface: "onboarding", source });
            if (source === "user") {
              setThinking(true);
              setHeardTick(Date.now()); // dismisses the silent-mic rescue: the agent heard them
            } else setThinking(false);
          },
          onDisconnect: () => { if (!endedByUserRef.current) beginClose(); },
          onError: (message) => {
            // Non-fatal mid-call errors (e.g. a tool hiccup) are narrated by the
            // agent itself and the call continues — only fail the UI if the
            // session never became live. Disconnect still yields the countdown.
            log.error("voice conversation error", { message });
            setPhase((p) => {
              if (p === "live" || p === "ended" || p === "post") return p;
              setErrMsg("The voice agent hit a snag. Please try again.");
              return "error";
            });
          },
        }));
        if (cancelled) { session.endSession(); return; }
        sessionRef.current = session;
        gvMark("session_live");
        setPhase("live");
        if (anonymous) {
          // hard stop: an unauthenticated stranger doesn't get an open line
          anonTimerRef.current = setTimeout(() => {
            try { session.endSession(); } catch (e) { /* already closed */ }
          }, ANON_MAX_MS);
        }
      } catch (err) {
        log.error("voice onboarding start failed", { error: err });
        if (!cancelled) {
          setErrMsg(err && err.name === "NotAllowedError"
            ? "Microphone access was denied — allow it and try again."
            : "Couldn't start the voice agent. Please try again.");
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (anonTimerRef.current) { clearTimeout(anonTimerRef.current); anonTimerRef.current = null; }
      if (sessionRef.current) { try { sessionRef.current.endSession(); } catch { /* closed */ } sessionRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, runSeq]);

  // keep latest profile visible to clientTools closures
  const profileRef = useRef(null);
  profileRef.current = profile;

  // --- ended countdown ---
  const beginClose = useCallback(() => { setPhase("ended"); setCountdown(CLOSE_SECONDS); }, []);
  useEffect(() => {
    if (phase !== "ended") return undefined;
    if (countdown <= 0) { if (onEndRef.current) onEndRef.current(); return undefined; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const stop = useCallback(async () => {
    endedByUserRef.current = true;
    if (sessionRef.current) { try { await sessionRef.current.endSession(); } catch { /* closed */ } sessionRef.current = null; }
    if (onEndRef.current) onEndRef.current();
  }, []);

  const restart = useCallback((intent) => {
    endedByUserRef.current = false;
    intentRef.current = intent;
    setRunSeq((s) => s + 1);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!panel || panel.mode !== "edit") return;
    setPanelErr("");
    const cur = profile || {};
    const merged = {
      objective: panel.field === "objective" ? panel.text : (cur.objective || ""),
      offering: panel.field === "offering" ? panel.text : (cur.offering || ""),
    };
    if (anonymous) {
      // same destination as the agent's save: the draft
      writeVoiceDraft(merged);
      setProfile((p) => ({ ...(p || {}), ...merged, has_profile: true }));
      setPanel({ mode: "view", field: panel.returnField || panel.field });
      setPanelSaved(true);
      setTimeout(() => setPanelSaved(false), 4000);
      try { sessionRef.current && sessionRef.current.sendContextualUpdate(
        `The member manually saved their new ${panel.field}: "${panel.text}". Acknowledge briefly and continue.`); } catch { /* optional */ }
      return;
    }
    try {
      if (cur.is_attendee) {
        await api.put(API_PATHS.EVENTS.UPDATE.replace("{eventId}", GLONET_AI_EVENT_ID), merged);
      } else {
        // not an attendee yet (free tier verified before the session started):
        // register — the update endpoint would 404 for them
        await api.post(
          API_PATHS.EVENTS.REGISTER
            .replace("{eventId}", GLONET_AI_EVENT_ID)
            .replace("{invitation_code}", cur.invitation_code),
          merged);
      }
      setProfile((p) => ({ ...(p || {}), ...merged, is_attendee: true, has_profile: true }));
      setPanel({ mode: "view", field: panel.returnField || panel.field });
      setPanelSaved(true);
      setTimeout(() => setPanelSaved(false), 4000);
      try { sessionRef.current && sessionRef.current.sendContextualUpdate(
        `The member manually saved their new ${panel.field}: "${panel.text}". Acknowledge briefly and continue.`); } catch { /* optional */ }
    } catch (err) {
      log.error("manual save failed", { error: err });
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setPanelErr(typeof detail === "string" ? detail : "Couldn't save — please try again.");
    }
  }, [panel, profile, anonymous]);

  // Live mic switch from the silent-mic selector — the session keeps running
  // (same session.changeInputDevice the game's VoiceDM uses).
  const changeMic = useCallback(async (deviceId) => {
    const s = sessionRef.current;
    if (s && typeof s.changeInputDevice === "function") {
      try {
        await s.changeInputDevice(deviceId ? { inputDeviceId: deviceId } : {});
      } catch (e) {
        log.error("mic switch failed", { error: e });
      }
    }
  }, []);

  const statusText =
    phase === "starting" ? "Connecting…"
      : phase === "error" ? errMsg
      : speaking ? "Agent speaking — jump in any time"
      : thinking ? "Thinking" : "Listening…";

  return (
    <div>
      {/* control strip */}
      {(phase === "starting" || phase === "live" || phase === "error") && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{
              flex: "none", width: 46, height: 46, borderRadius: "50%",
              background: phase === "live" ? ACCENT : "rgba(255,255,255,0.08)",
              display: "flex", alignItems: "center", justifyContent: "center",
              animation: phase === "live" ? "ga-pulse 1.8s infinite" : "none",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   stroke={phase === "live" ? "#0c0d10" : "#f1f2f4"} strokeWidth="1.8" strokeLinecap="round">
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11 a7 7 0 0 0 14 0" />
                <line x1="12" y1="18" x2="12" y2="21" />
              </svg>
            </span>
            <Bars active={phase === "live" && !thinking} />
            <button onClick={stop} style={{ marginLeft: "auto", flex: "none", fontFamily: MONO, fontSize: 11, letterSpacing: "0.1em", color: "#7c818b", background: "none", border: "none", cursor: "pointer" }}>
              {phase === "error" ? "CLOSE" : "STOP"}
            </button>
          </div>
          <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.55, color: phase === "error" ? "#e0a35e" : "#dfe1e5", display: "flex", alignItems: "center" }}>
            {statusText}{thinking && phase === "live" && <ThinkingDots />}
          </div>
          {/* silent-mic rescue: invisible until nothing has been heard for
              micSilenceSecs, then a picker appears (BlackHole/Zoom/Meet leave
              the browser on dead inputs); hides itself once speech registers */}
          <MicSelector live={phase === "live"} silenceSecs={micSilenceSecs} onDeviceChange={changeMic}
                       holdOff={speaking || thinking} heardTs={heardTick} />
        </div>
      )}

      {/* post-call countdown */}
      {phase === "ended" && (
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 14, color: "#dfe1e5" }}>
            All set — closing session in {countdown}s
          </div>
          <div style={{ fontSize: 12, color: "#7c818b", marginTop: 4 }}>
            {anonymous
              ? "Your answers are saved on this device — sign in and the matchmaker gets to work."
              : "Anything you changed by voice saves automatically within a minute."}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button onClick={() => setPhase("post")} style={pillBtn(false)}>Don't close</button>
            <button onClick={stop} style={pillBtn(true)}>Close now</button>
          </div>
        </div>
      )}

      {/* keep-open options */}
      {phase === "post" && (
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 14, color: "#dfe1e5" }}>Want to change anything?</div>
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button onClick={() => restart("objective")} style={pillBtn(true)}>Update objective</button>
            <button onClick={() => restart("offering")} style={pillBtn(true)}>Update offering</button>
            <button onClick={() => restart("both")} style={pillBtn(false)}>Both</button>
            <button onClick={stop} style={pillBtn(false)}>Done</button>
          </div>
        </div>
      )}

      {/* view / edit panels (agent client tools) */}
      {panel && panel.mode === "view" && profile && (() => {
        const vf = panel.field || "both";
        const showObj = vf === "both" || vf === "objective";
        const showOff = vf === "both" || vf === "offering";
        return (
          <div style={panelStyle}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.14em", color: ACCENT, textTransform: "uppercase" }}>
              {vf === "both" ? "Your matching info" : `Your ${vf}`}
            </div>
            <div style={{ marginTop: 8, fontSize: 13.5, color: "#cfd2d8", lineHeight: 1.55 }}>
              {showObj && <span><b>Objective:</b> {profile.objective || <i style={{ color: "#7c818b" }}>not set yet</i>}{showOff && <br />}</span>}
              {showOff && <span><b>Offering:</b> {profile.offering || <i style={{ color: "#7c818b" }}>not set yet</i>}</span>}
            </div>
            {panelSaved && <div style={{ marginTop: 8, fontSize: 12.5, color: "#57d9a4" }}>Saved ✓</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {showObj && <button onClick={() => { setPanelErr(""); setPanel({ mode: "edit", field: "objective", returnField: vf, text: profile.objective || "" }); }} style={pillBtn(false)}>Edit objective</button>}
              {showOff && <button onClick={() => { setPanelErr(""); setPanel({ mode: "edit", field: "offering", returnField: vf, text: profile.offering || "" }); }} style={pillBtn(false)}>Edit offering</button>}
              <button onClick={() => setPanel(null)} style={pillBtn(false)}>Hide</button>
            </div>
          </div>
        );
      })()}
      {panel && panel.mode === "edit" && (
        <div style={panelStyle}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.14em", color: ACCENT, textTransform: "uppercase" }}>Edit {panel.field}</div>
          <textarea
            value={panel.text}
            onChange={(e) => {
              // capture NOW — e.target is a live node; reading it inside the
              // queued updater returns the restored (old) controlled value
              const text = e.target.value;
              setPanel((p) => ({ ...p, text }));
            }}
            rows={4}
            style={{ width: "100%", marginTop: 8, fontFamily: "inherit", fontSize: 14, lineHeight: 1.5, color: "#f1f2f4", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: 10, outline: "none", resize: "vertical" }}
          />
          {panelErr && <div style={{ marginTop: 8, fontSize: 12.5, color: "#e0a35e" }}>{panelErr}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={saveEdit} style={pillBtn(true)}>Save</button>
            <button onClick={() => { setPanelErr(""); setPanel(null); }} style={pillBtn(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
